import createDebug from "debug";
import { AuthConfig } from "../interface-definitions/index";
import { EnvironmentProviders, makeEnvironmentProviders } from "@angular/core";
import {
  AbstractSecurityStorage,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader,
  provideAuth as provideOidcAuth,
} from "angular-auth-oidc-client";
import { from } from "rxjs";

const debug = createDebug("provideAuth");

export function provideAuth(): EnvironmentProviders {
  debug("provideAuth called");

  class ConfigurableSecurityStorage implements AbstractSecurityStorage {
    private storage: Storage = sessionStorage;

    configure(type: "sessionStorage" | "localStorage") {
      this.storage = type === "localStorage" ? localStorage : sessionStorage;
    }

    read(key: string): string | null {
      return this.storage.getItem(key);
    }

    write(key: string, value: string): void {
      this.storage.setItem(key, value);
    }

    remove(key: string): void {
      this.storage.removeItem(key);
    }

    clear(): void {
      this.storage.clear();
    }
  }

  const configurableStorage = new ConfigurableSecurityStorage();

  // Stub config used when the server reports auth is disabled (or the
  // config endpoint is unreachable). angular-auth-oidc-client validates
  // that `authority` is set on whatever config it receives, so returning
  // `{}` makes it log an error on every bootstrap. We satisfy the
  // validator with a benign placeholder; nothing in this provider triggers
  // an auto-login, so the OIDC client stays idle.
  const disabledConfig: OpenIdConfiguration = {
    authority: window.location.origin,
    clientId: "auth-disabled",
    redirectUrl: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    scope: "openid",
    responseType: "code",
    unauthorizedRoute: "/login",
    forbiddenRoute: "/not-authorized",
  };

  const loadConfig = async (): Promise<OpenIdConfiguration> => {
    try {
      // Relative to <base href> so a configured basePath (e.g.
      // /apexDesigner) is honored — a leading-slash URL would bypass it.
      const response = await fetch("api/auth/config");
      debug("response.ok", response.ok);

      if (!response.ok) {
        debug("config endpoint failed");
        return disabledConfig;
      }

      const config: AuthConfig | null = await response.json();
      debug("config", config);

      if (!config) {
        debug("auth disabled");
        return disabledConfig;
      }

      if (config.storage) {
        configurableStorage.configure(config.storage);
      }

      const oidc: OpenIdConfiguration = {
        authority: `https://${config.domain}`,
        redirectUrl: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        clientId: config.clientId,
        scope: (config.scopes ?? ["openid", "profile", "email"]).join(" "),
        responseType: "code",
        silentRenew: true,
        useRefreshToken: true,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        customParamsAuthRequest: { audience: config.audience },
        useCustomAuth0Domain: config.useCustomAuth0Domain,
        // angular-auth-oidc-client defaults `unauthorizedRoute` to
        // '/unauthorized' and `forbiddenRoute` to '/forbidden' — routes no
        // generated app emits. The code-flow callback navigates to
        // `unauthorizedRoute` when the token exchange fails, which crashed
        // the router with NG04002 instead of showing anything. Point it at
        // the same login path the role guard redirects to, so both halves of
        // the rejection path agree and the visitor can retry signing in.
        // These are router URLs, so a leading slash is correct even under a
        // configured basePath — the router already works below <base href>.
        unauthorizedRoute: config.loginPath ?? "/login",
        // Unread in angular-auth-oidc-client 21, but set so a future version
        // that wires it up lands on the page that carries this message.
        forbiddenRoute: "/not-authorized",
      };

      if (config.logoutUrl) {
        const logoutUrl = new URL(config.logoutUrl);
        logoutUrl.searchParams.set("client_id", config.clientId);
        logoutUrl.searchParams.set("returnTo", window.location.origin);
        oidc.authWellknownEndpoints = {
          endSessionEndpoint: logoutUrl.toString(),
        };
      }

      return oidc;
    } catch {
      debug("config not available");
      return disabledConfig;
    }
  };

  return makeEnvironmentProviders([
    provideOidcAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: () => new StsConfigHttpLoader(from(loadConfig())),
      },
    }),
    { provide: AbstractSecurityStorage, useValue: configurableStorage },
  ]);
}
